import { createSelector } from 'reselect'
import { User } from '@graphics/gfx-common/dist/types'
import {
  IAuth,
  IPermissionsSelector,
  IAreaSelector,
  IPermissions,
} from '../types/auth'
import { AppStateType } from '../reducers'
import {
  RIGHT_CONSTANTS,
  retrieveGroupId,
  accessibleAreasPerRoleAndAction,
} from '../services/auth'

export const selectAuth = ({ auth }: AppStateType): IAuth => auth

export const permissionsSelector = (
  state: AppStateType,
  props: IPermissionsSelector
): IPermissions => {
  const { permissions } = selectAuth(state)
  const groupId = retrieveGroupId(props)
  const isGroupEditorOnly =
    permissions.editorForGroups.includes(groupId) &&
    !permissions.isSystemAdmin &&
    !permissions.isSystemApprover &&
    !permissions.isSystemSupport
  return {
    ...permissions,
    restrictedAreasReadable: isGroupEditorOnly
      ? accessibleAreasPerRoleAndAction[
          RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION
        ][RIGHT_CONSTANTS.READ]
      : permissions.restrictedAreasReadable,
    restrictedAreasWritable: isGroupEditorOnly
      ? accessibleAreasPerRoleAndAction[
          RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION
        ][RIGHT_CONSTANTS.WRITE]
      : permissions.restrictedAreasWritable,
  }
}

export const areaReadableSelector = (
  state: AppStateType,
  props: IAreaSelector
): boolean => {
  const { restrictedAreasReadable } = permissionsSelector(state, props)
  return restrictedAreasReadable.includes(props.area)
}

export const areaWritableSelector = (
  state: AppStateType,
  props: IAreaSelector
): boolean => {
  const { restrictedAreasWritable } = permissionsSelector(state, props)
  return restrictedAreasWritable.includes(props.area)
}

export const selectToken = createSelector(
  selectAuth,
  ({ token }) => token
)

export const selectUserData = createSelector(
  selectAuth,
  ({ uid = '', family_name = '', given_name = '' }): { user: User } => ({
    user: {
      id: uid,
      name:
        (given_name || family_name) && `${given_name} ${family_name}`.trim(),
    },
  })
)

export const selectPermissions = createSelector(
  permissionsSelector,
  (permissions: IPermissions): IPermissions => permissions
)

export const selectSettingsAccess = createSelector(
  permissionsSelector,
  ({
    isSystemAdmin,
    isSystemApprover,
    isSystemSupport,
  }: IPermissions): boolean =>
    isSystemAdmin || isSystemApprover || isSystemSupport
)

export const selectAreaReadable = createSelector(
  areaReadableSelector,
  (canViewArea: boolean): boolean => canViewArea
)

export const selectAreaWritable = createSelector(
  areaWritableSelector,
  (canEditArea: boolean): boolean => canEditArea
)

export const selectIsSystemApprover = createSelector(
  selectAuth,
  ({ permissions: { isSystemApprover } }) => isSystemApprover
)
